/**
 * Untappd rating fallback handler
 *
 * Hydrates `.js-untappd-rating[data-untappd-url]` badges that were rendered
 * server-side in a "loading" state, by fetching the live rating from a
 * Cloudflare worker and updating the badge in place.
 *
 * Loaded globally from layout/theme.liquid so it's available on first page
 * load AND across AJAX-driven navigations (collection filter / sort /
 * pagination, quick-shop content, predictive search results, etc.). Inline
 * <script> tags inside HTML inserted via innerHTML do not execute, so we
 * rely on a MutationObserver to pick up newly inserted badges.
 */
(function () {
  if (window.__untappdRatingFallback) return;

  var ENDPOINT = 'https://hoppenaar-untappd.colin-43f.workers.dev/untappd?url=';
  var SELECTOR = '.js-untappd-rating[data-untappd-url]';
  var cache = new Map(); // dedupe by URL across the page lifecycle

  function updateDecimal(rating) {
    var decimal = rating % 1;
    if (decimal >= 0.3 && decimal <= 0.7) return 0.5;
    if (decimal > 0.7) return 1;
    return 0;
  }

  function renderError(node) {
    var label = node.querySelector('[data-untappd-rating-text]');
    if (label) label.textContent = 'Untappd rating niet beschikbaar';
    // Leave the badge hidden — we don't want to show a "niet beschikbaar"
    // message in the UI, the wrapper just stays out of view.
  }

  function updateNode(node, data) {
    var rating = Number(data && data.rating);
    var count =
      (data && (data.count != null ? data.count : data.rating_count != null ? data.rating_count : data.reviews)) ||
      null;
    var label = node.querySelector('[data-untappd-rating-text]');
    var visual = node.querySelector('[data-untappd-rating-visual]');
    var star = node.querySelector('[data-untappd-rating-star]');
    var countNode = node.querySelector('[data-untappd-rating-count]');

    if (!Number.isFinite(rating) || rating <= 0) {
      renderError(node);
      return;
    }

    if (visual) {
      visual.hidden = false;
      visual.setAttribute('aria-label', 'Untappd rating ' + rating.toFixed(2) + ' out of 5');
    }
    if (star) {
      star.style.setProperty('--rating', Math.floor(rating));
      star.style.setProperty('--rating-max', 5);
      star.style.setProperty('--rating-decimal', updateDecimal(rating));
    }
    if (label) {
      label.textContent = 'Untappd ' + rating.toFixed(2) + ' / 5';
    }
    if (countNode && count) {
      countNode.hidden = false;
      countNode.innerHTML =
        '<span aria-hidden="true">(' + count + ')</span>' +
        '<span class="visually-hidden">' + count + '</span>';
    }

    // Reveal the badge now that we have a valid rating to display.
    node.hidden = false;
  }

  function processNode(node) {
    if (!node || node.dataset.untappdLoaded === 'true') return;
    node.dataset.untappdLoaded = 'true';

    var url = node.dataset.untappdUrl;
    if (!url) {
      renderError(node);
      return;
    }

    var promise = cache.get(url);
    if (!promise) {
      promise = fetch(ENDPOINT + encodeURIComponent(url)).then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      });
      cache.set(url, promise);
    }

    promise.then(function (data) { updateNode(node, data); }).catch(function () { renderError(node); });
  }

  function processAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SELECTOR).forEach(processNode);
  }

  function startObserver() {
    if (!document.body || typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(SELECTOR)) {
            processNode(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll(SELECTOR).forEach(processNode);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.__untappdRatingFallback = { processAll: processAll, processNode: processNode };

  function init() {
    processAll();
    startObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
